import VueRouter, { RouteConfig } from 'vue-router';
import AccessExpiredView from '@/views/access-expired/access-expired-view.vue';
import ActivateView from '@/views/activate/activate-view.vue';
import BlacklistView from '@/views/blacklist/blacklist-view.vue';
import ChangePasswordView from '@/views/change-password/change-password-view.vue';
import CompanyView from '@/views/company/company-view.vue';
import DashboardView from '@/views/dashboard/dashboard-view.vue';
import Error404View from '@/views/error-404/error-404-view.vue';
import HelpView from '@/views/help/help-view.vue';
import i18n from '@/localization/i18n';
import ImpersonateView from '@/views/impersonate/impersonate-view.vue';
import InvalidSessionView from '@/views/invalid-session/invalid-session-view.vue';
import ListView from '@/views/list-view/list-view.vue';
import PrintProcurementView from '@/views/print-procurement/print-procurement-view.vue';
import PrintProjectView from '@/views/print-project/print-project-view.vue';
import ProcurementView from '@/views/procurement-view/procurement-view.vue';
import ProjectView from '@/views/project-view/project-view.vue';
import { RouterNames } from '@/util/enums/router-names';
import SearchCompanyView from '@/views/search-company/search-company-view.vue';
import SearchView from '@/views/search/search-view.vue';
import UserSettingsView from '@/views/user-settings/user-settings-view.vue';
import Vue from 'vue';

/**
 * @author Jais Christiansen
 */

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
  // Dashboard (Logged In)
  {
    path: '',
    name: RouterNames.LOGGED_IN_DASHBOARD,
    component: DashboardView,
    meta: { title: 'pages.dashboard.pageTitle', header: 'compact', requireLogin: true },
  },

  {
    path: '/dashboard',
    redirect: { name: RouterNames.LOGGED_IN_DASHBOARD },
  },

  // Search (Logged In)
  {
    path: '/search/:page',
    name: RouterNames.LOGGED_IN_SEARCH,
    component: SearchView,
    props: (route) => ({
      ...route.params,
    }),
    meta: { title: 'pages.search.pageTitle', autoscroll: true, header: 'compact', requireLogin: true },
  },

  // Project View (Logged In)
  {
    path: '/project/:id',
    name: RouterNames.LOGGED_IN_PROJECT_VIEW,
    props: true,
    component: ProjectView,
    meta: { title: 'pages.project.pageTitle', limitedAccess: true, header: 'compact', requireLogin: true },
  },

  // Procurement View (Logged In)
  {
    path: '/procurement/:id',
    name: RouterNames.LOGGED_IN_PROCUREMENT_VIEW,
    props: true,
    component: ProcurementView,
    meta: { title: 'pages.procurement.pageTitle', header: 'compact', autoscroll: true, requireLogin: true },
  },

  // Blacklist (Logged In)
  {
    path: '/blacklist',
    name: RouterNames.LOGGED_IN_BLACKLIST_VIEW,
    component: BlacklistView,
    meta: { title: 'pages.blacklist.pageTitle', header: 'compact', requireLogin: true },
  },

  // Settings (Logged In)
  {
    path: '/settings',
    name: RouterNames.LOGGED_IN_USER_SETTINGS,
    component: UserSettingsView,
    meta: { title: 'pages.userSettings.pageTitle', header: 'compact', requireLogin: true },
  },

  // List view (Logged In)
  {
    path: '/list/:id',
    name: RouterNames.LOGGED_IN_LIST_VIEW,
    props: true,
    component: ListView,
    meta: { title: 'pages.lists.pageTitle', header: 'compact', requireLogin: true },
  },

  // Company Search (Logged In)
  {
    path: '/search-company',
    name: RouterNames.LOGGED_IN_COMPANY_SEARCH,
    component: SearchCompanyView,
    meta: { title: 'pages.companySearch.pageTitle', autoscroll: true, header: 'compact', requireLogin: true },
  },

  // Company View (Logged In)
  {
    path: '/company/:id',
    name: RouterNames.LOGGED_IN_COMPANY_VIEW,
    props: true,
    component: CompanyView,
    meta: { title: 'pages.company.pageTitle', header: 'compact', requireLogin: true },
  },

  // Access expired (Logged In)
  {
    path: '/access-expired',
    name: RouterNames.LOGGED_IN_ACCESS_EXPIRED,
    component: AccessExpiredView,
    meta: { title: 'pages.accessExpired.pageTitle', limitedAccess: true, header: 'compact', requireLogin: true },
  },

  // Change password
  {
    path: '/change-password/:resetToken',
    name: RouterNames.LOGGED_OUT_CHANGE_PASSWORD,
    component: ChangePasswordView,
    props: (route) => ({
      ...route.params,
    }),
    meta: { title: 'pages.changePassword.pageTitle', requireLogin: false },
  },

  // Invalid Session
  {
    path: '/invalid-session/',
    name: RouterNames.INVALID_SESSION,
    component: InvalidSessionView,
    meta: { title: 'pages.accessExpired.pageTitle', header: 'compact', requireLogin: false },
  },

  // Activate
  {
    path: '/activate/:activateToken',
    name: RouterNames.LOGGED_OUT_ACTIVATE,
    component: ActivateView,
    props: (route) => ({
      ...route.params,
    }),
    meta: { title: 'pages.activate.pageTitle', header: 'compact', requireLogin: false },
  },

  // Imporsanate
  {
    path: '/impersonate/:token',
    name: RouterNames.LOGGED_OUT_IMPERSONATE_USER,
    component: ImpersonateView,
    props: (route) => ({
      ...route.params,
    }),
    meta: { title: 'pages.impersonate.pageTitle', requireLogin: false },
  },

  // Print Procurement (Logged In)
  {
    path: '/print-procurement/:id',
    name: RouterNames.PRINT_PROCUREMENT,
    props: true,
    component: PrintProcurementView,
    meta: { title: 'pages.procurement.pageTitle', hideHeaderFooter: true, requireLogin: true },
  },

  // Print Project (Logged In)
  {
    path: '/print-project/:id',
    name: RouterNames.PRINT_PROJECT,
    props: true,
    component: PrintProjectView,
    meta: { title: 'pages.project.pageTitle', hideHeaderFooter: true, requireLogin: true },
  },

  // Help (Logged In)
  {
    path: '/help',
    name: RouterNames.LOGGED_IN_HELP,
    component: HelpView,
    meta: { title: 'pages.help.pageTitle', header: 'compact', requireLogin: true },
  },

  // 404
  {
    path: '*',
    name: RouterNames.ERROR_NOT_FOUND,
    component: Error404View,
    meta: { title: 'pages.error404.pageTitle', requireLogin: false },
  },
]

const router = new VueRouter({
  mode: 'history',
  routes: routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.name === RouterNames.LOGGED_IN_SEARCH && from.name === RouterNames.LOGGED_IN_SEARCH) {
      return null;
    }

    if (to.meta?.autoscroll === true && savedPosition != null) {
      return savedPosition;
    } else if (to.hash) {
      return { selector: to.hash };
    }

    return { x: 0, y: 0 };
  },
});

// Authentication guard for routes with authentication required
router.beforeEach((to, from, next) => {
  document.querySelectorAll('div[role=tooltip]').forEach(element => {
    element.remove();
  })
  if (to.path == null) {
    next({ name: RouterNames.LOGGED_IN_DASHBOARD })
  }
  next();
});

router.afterEach((to, from) => {
  Vue.nextTick(() => {
    document.title = (i18n.t(to.meta.title) as string) || to.name;
  });
});

export default router;
